<template>
  <div class="thh-card">
    <header class="thh-header">
      <div>
        <h5 class="thh-title">Densidade por hora × local</h5>
        <p class="thh-subtitle">
          Onde e quando os check-ins acontecem. Cor mais quente = mais entradas.
        </p>
      </div>
      <div class="thh-legend" v-if="hasData">
        <span class="thh-legend-label">menos</span>
        <span class="thh-legend-gradient"></span>
        <span class="thh-legend-label">mais</span>
      </div>
    </header>

    <div v-if="!hasData" class="thh-empty">
      <i class="bx bx-grid-alt thh-empty-icon"></i>
      <p>Sem dados de densidade no período</p>
      <small>Os blocos colorem por intensidade de leituras conforme acontecem</small>
    </div>

    <div v-else class="thh-chart-wrap" :style="{ height: chartHeight + 'px' }">
      <div ref="chart" class="thh-chart"></div>
    </div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'tag-hour-heatmap',
  props: {
    heatmap: { type: Object, default: () => ({ tags: [], matrix: {} }) }
  },
  data: () => ({
    chartInstance: null,
    resizeObserver: null
  }),
  computed: {
    tags () {
      return (this.heatmap && this.heatmap.tags) || []
    },
    hasData () {
      return this.tags.length > 0
    },
    chartHeight () {
      // Altura proporcional ao nº de tags, com mínimo e máximo razoáveis.
      // 36px por tag + 60px de padding pros eixos.
      return Math.max(220, Math.min(560, this.tags.length * 36 + 80))
    },
    /**
     * Transforma a matriz `{ tagName: { hour: count } }` em formato ECharts:
     * array de [xIndex, yIndex, value]. Eixo X = horas (0-23). Eixo Y = tags.
     */
    chartData () {
      const data = []
      let maxValue = 0
      const matrix = (this.heatmap && this.heatmap.matrix) || {}
      this.tags.forEach((tagName, yIdx) => {
        for (let hour = 0; hour < 24; hour++) {
          const cnt = (matrix[tagName] && matrix[tagName][hour]) || 0
          data.push([hour, yIdx, cnt])
          if (cnt > maxValue) maxValue = cnt
        }
      })
      return { data, maxValue }
    }
  },
  watch: {
    heatmap: {
      deep: true,
      handler () { this.$nextTick(() => this.renderChart()) }
    }
  },
  mounted () {
    if (this.hasData) this.renderChart()
    this.attachResize()
  },
  beforeDestroy () {
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
    this.detachResize()
  },
  methods: {
    renderChart () {
      const el = this.$refs.chart
      if (!el || !this.hasData) return
      if (!this.chartInstance) {
        this.chartInstance = echarts.init(el)
      }
      const { data, maxValue } = this.chartData
      const hours = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}h`)

      this.chartInstance.setOption({
        tooltip: {
          position: 'top',
          backgroundColor: 'rgba(26, 29, 38, 0.95)',
          borderColor: 'transparent',
          textStyle: { color: '#fff', fontSize: 12 },
          extraCssText: 'border-radius: 8px; padding: 8px 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);',
          formatter: (params) => {
            const tagName = this.tags[params.value[1]] || '—'
            const hour = String(params.value[0]).padStart(2, '0')
            const cnt = params.value[2]
            if (cnt === 0) return `${tagName} · ${hour}h<br/><span style="opacity:.7">sem leituras</span>`
            return `<strong>${tagName}</strong> · ${hour}h<br/><strong style="color:#8b5cf6">${cnt}</strong> ${cnt === 1 ? 'check-in' : 'check-ins'}`
          }
        },
        grid: {
          left: 100,
          right: 30,
          top: 10,
          bottom: 40,
          containLabel: false
        },
        xAxis: {
          type: 'category',
          data: hours,
          axisLine: { lineStyle: { color: '#eaecf2' } },
          axisTick: { show: false },
          axisLabel: {
            color: '#8b90a5',
            fontSize: 10,
            interval: 1,
            margin: 12
          },
          splitArea: { show: false }
        },
        yAxis: {
          type: 'category',
          data: this.tags,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#1a1d26',
            fontSize: 11,
            fontWeight: 600,
            margin: 14,
            formatter: (val) => val.length > 14 ? val.substring(0, 13) + '…' : val
          },
          splitArea: { show: false }
        },
        visualMap: {
          show: false, // legend custom no header
          min: 0,
          max: Math.max(1, maxValue),
          calculable: false,
          inRange: {
            // Gradiente: cinza muito claro → azul → roxo intenso
            color: ['#f5f6fa', '#dde3ff', '#a4b3f5', '#6e7fe6', '#8b5cf6', '#6d28d9']
          }
        },
        series: [{
          type: 'heatmap',
          data: data,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2
          },
          emphasis: {
            itemStyle: {
              borderColor: '#1a1d26',
              borderWidth: 2,
              shadowBlur: 8,
              shadowColor: 'rgba(85, 110, 230, 0.4)'
            }
          },
          progressive: 1000,
          animationDuration: 600,
          animationEasing: 'cubicOut'
        }]
      }, true)
    },
    attachResize () {
      if (typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', this.handleResize)
        return
      }
      this.resizeObserver = new ResizeObserver(() => this.handleResize())
      if (this.$refs.chart) this.resizeObserver.observe(this.$refs.chart)
    },
    detachResize () {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      } else {
        window.removeEventListener('resize', this.handleResize)
      }
    },
    handleResize () {
      if (this.chartInstance) this.chartInstance.resize()
    }
  }
}
</script>

<style lang="scss" scoped>
.thh-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 22px 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
}

.thh-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.thh-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0 0 4px 0;
}

.thh-subtitle {
  font-size: 12px;
  color: #8b90a5;
  margin: 0;
  max-width: 480px;
}

.thh-legend {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.thh-legend-label {
  font-size: 10px;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.thh-legend-gradient {
  display: inline-block;
  width: 90px;
  height: 8px;
  border-radius: 99px;
  background: linear-gradient(90deg,
    #f5f6fa 0%,
    #dde3ff 20%,
    #a4b3f5 40%,
    #6e7fe6 60%,
    #8b5cf6 80%,
    #6d28d9 100%
  );
}

.thh-empty {
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8b90a5;
  text-align: center;

  .thh-empty-icon {
    font-size: 42px;
    color: #d0d5dd;
    margin-bottom: 8px;
  }
  p { font-size: 13px; font-weight: 600; margin: 0 0 4px 0; color: #4a5068; }
  small { font-size: 12px; max-width: 360px; }
}

.thh-chart-wrap {
  width: 100%;
  position: relative;
}

.thh-chart {
  width: 100%;
  height: 100%;
}
</style>
